import type { Component, Ref } from "vue"
import {
  FeatureUpsellGraphicsThreatManagement,
  FeatureUpsellGraphicsTeamsSlack,
  FeatureUpsellGraphicsSpearPhishing,
  FeatureUpsellGraphicsSmishingQuishing,
  FeatureUpsellGraphicsSimulationBuilder,
  FeatureUpsellGraphicsRiskBasedTraining,
  FeatureUpsellGraphicsPolicyManagement,
  FeatureUpsellGraphicsIndustryBenchmarking,
  FeatureUpsellGraphicsHumanRiskIntelligence,
  FeatureUpsellGraphicsComplianceIntegration,
  FeatureUpsellGraphicsBringYourOwnContent,
  FeatureUpsellGraphicsPhishingReporting,
  FeatureUpsellGraphicsGoalSetting,
} from "#components"
import type { FeatureTiersResponse } from "~/server/routes/admin/v1/workspace/feature-tiers.ts"
export type UpsellFeatureConfig = {
  graphicComponent: Component
  knowledgeBaseLink?: string
  accessFeatureFlags: Feature[]
}

type FeatureAccessConfig = UpsellFeatureConfig & {
  title: string
  summary: string
  highlights: string[]
}

type UseFeatureAccessResult = {
  hasFeatureAccess: Ref<boolean>
  featureTiers: Ref<FeatureTiersResponse | undefined>
  isFeatureHidden: Ref<boolean>
  showUpsell: Ref<boolean>
  featureConfig: Ref<FeatureAccessConfig>
}

const upsellFeatures = {
  "simulation-builder": {
    graphicComponent: FeatureUpsellGraphicsSimulationBuilder,
    accessFeatureFlags: [
      "admin:simulations:templates",
      "admin:phishing:simulation-builder",
    ],
  },
  "risk-based-training": {
    graphicComponent: FeatureUpsellGraphicsRiskBasedTraining,
    accessFeatureFlags: ["admin:risk", "admin:training:risk-based-training"],
  },
  "threat-management": {
    graphicComponent: FeatureUpsellGraphicsThreatManagement,
    knowledgeBaseLink: "https://help.moxso.com/en/threat-management",
    accessFeatureFlags: ["admin:threats", "admin:security:threat-management"],
  },
  "bring-your-own-content": {
    graphicComponent: FeatureUpsellGraphicsBringYourOwnContent,
    accessFeatureFlags: [
      "admin:training:bring-your-own-content",
      "admin:videos",
      "admin:courses",
    ],
  },
  "policy-management": {
    graphicComponent: FeatureUpsellGraphicsPolicyManagement,
    accessFeatureFlags: ["admin:policies", "admin:training:policy-management"],
  },
  "spear-phishing": {
    graphicComponent: FeatureUpsellGraphicsSpearPhishing,
    accessFeatureFlags: [
      "admin:simulations:software-targeting",
      "admin:simulations:ceo-fraud",
      "admin:phishing:spear-phishing",
    ],
  },
  smishing: {
    graphicComponent: FeatureUpsellGraphicsSmishingQuishing,
    accessFeatureFlags: ["admin:simulations:sms", "admin:phishing:smishing"],
  },
  "industry-benchmarking": {
    graphicComponent: FeatureUpsellGraphicsIndustryBenchmarking,
    accessFeatureFlags: ["admin:analytics:industry-benchmarking"],
  },
  "teams-slack-integration": {
    knowledgeBaseLink:
      "https://help.moxso.com/en/how-do-i-set-up-slack-notifications",
    graphicComponent: FeatureUpsellGraphicsTeamsSlack,
    accessFeatureFlags: [
      "admin:slack:notifications",
      "admin:microsoft:notifications",
      "admin:platform:teams-slack-integration",
    ],
  },
  "compliance-integration": {
    graphicComponent: FeatureUpsellGraphicsComplianceIntegration,
    accessFeatureFlags: ["admin:platform:compliance-integration"],
  },
  "human-risk-intelligence": {
    knowledgeBaseLink: "https://help.moxso.com/en/human-risk-intelligence-hri",
    graphicComponent: FeatureUpsellGraphicsHumanRiskIntelligence,
    accessFeatureFlags: ["admin:human-risk-intelligence"],
  },
  "phishing-reporting-add-in": {
    knowledgeBaseLink:
      "https://help.moxso.com/en/how-do-i-set-up-the-reporting-add-in-for-resilience-pro-and-secure-users",
    graphicComponent: FeatureUpsellGraphicsPhishingReporting,
    accessFeatureFlags: [
      "admin:security:phishing-reporting-add-in",
      "admin:simulations:plugins",
    ],
  },
  "goal-setting": {
    graphicComponent: FeatureUpsellGraphicsGoalSetting,
    accessFeatureFlags: ["admin:platform:goal-setting"],
  },
} as const satisfies Record<string, UpsellFeatureConfig>

export type UpsellFeature = keyof typeof upsellFeatures

export function useFeatureAccess(
  feature: UpsellFeature,
): UseFeatureAccessResult {
  const { hasFeature } = useAuth()
  const { $api } = useNuxtApp()
  const { t, tm } = useI18n()
  const featureConfig = computed<UpsellFeatureConfig>(() => {
    return upsellFeatures[feature]
  })
  const hasFeatureAccess = computed<boolean>(() => {
    return upsellFeatures[feature].accessFeatureFlags.some((featureFlag) =>
      hasFeature(featureFlag),
    )
  })

  const { data: featureTiers } = useFetch<FeatureTiersResponse>(
    "/admin/v1/workspace/feature-tiers",
    {
      $fetch: $api,
      key: "workspace-feature-tiers",
      dedupe: "defer",
      getCachedData: (key, nuxtApp) =>
        nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
    },
  )

  const showUpsell = computed(() => {
    if (hasFeatureAccess.value || !featureTiers.value) {
      return false
    }

    if (!featureTiers.value?.current_tier) {
      return true
    }

    return featureConfig.value.accessFeatureFlags.some((featureFlag) =>
      featureTiers.value?.next_tier?.gained_features.includes(featureFlag),
    )
  })

  const isFeatureHidden = computed(() => {
    return !hasFeatureAccess.value && (!featureTiers.value || !showUpsell.value)
  })

  const highlights = computed<string[]>(() => {
    return tm(`dynamic_keys.feature_upsell.${feature}.items`)
  })

  return {
    hasFeatureAccess,
    featureTiers,
    isFeatureHidden,
    showUpsell,
    featureConfig: computed(() => ({
      ...featureConfig.value,
      title: t(`dynamic_keys.feature_upsell.${feature}.title`),
      summary: t(`dynamic_keys.feature_upsell.${feature}.summary`),
      highlights: highlights.value,
    })),
  }
}
